'use client';

import React, { useEffect, useRef } from 'react';
import styles from './DotLine.module.css';

// Component Props Interface
interface AnimationProps {
  rootMargin?: string;
  threshold?: number;
}

const DotLineAnimation: React.FC<AnimationProps> = ({
  rootMargin = '0px',
  threshold = 0.4,
}) => {
  const drawWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const drawWrapper = drawWrapperRef.current;

    // Animation function for dot-line
    const animateLineOnScroll = () => {
      if (!drawWrapper) return;

      const rect = drawWrapper.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      const startTrigger = windowHeight;
      const endTrigger = 132;

      const totalScrollDistance = startTrigger - endTrigger;
      const scrolledDistance = Math.min(
        Math.max(startTrigger - rect.top, 0),
        totalScrollDistance,
      );

      const scrollProgress = scrolledDistance / totalScrollDistance;
      const translateValue = Math.min(0, -100 + scrollProgress * 100); // ⬅️ Animate from -100% to 0%

      drawWrapper.style.transform = `translateX(${translateValue}%)`;
    };

    // Set up Intersection Observer for opacity animations
    const observerOptions = {
      root: null,
      rootMargin,
      threshold,
    };

    const opacityObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (
          entry.isIntersecting &&
          entry.target.getAttribute('data-animation') === 'opacity'
        ) {
          entry.target.classList.add('fade-in');
        }
      });
    }, observerOptions);

    // Observe opacity elements
    document
      .querySelectorAll('[data-animation="opacity"]')
      .forEach((element) => {
        opacityObserver.observe(element);
      });

    // Set up dot-line animation
    if (drawWrapper) {
      animateLineOnScroll();
      window.addEventListener('scroll', animateLineOnScroll, { passive: true });
      window.addEventListener('resize', animateLineOnScroll);
    }

    // Clean up event listeners and observers
    return () => {
      window.removeEventListener('scroll', animateLineOnScroll);
      window.removeEventListener('resize', animateLineOnScroll);
      opacityObserver.disconnect();
    };
  }, [rootMargin, threshold]);

  return (
    <div className={`${styles.drawArea} draw-a`}>
      <div
        ref={drawWrapperRef}
        className={`${styles.drawWrapper} draw-w`}
        data-animation="dot-line"
      >
        <div className={`${styles.line} line`} />
        <div className={`${styles.dot} dot`} />
      </div>
    </div>
  );
};

// Example component with both animations
export const AnimationContainer: React.FC = () => {
  return (
    <div className={styles.globalStyle}>
      <DotLineAnimation />
    </div>
  );
};
